import Box from '@/common/components/Box';
import { ImageCarousel } from '@/common/components/ImageCarousel';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import SmallBadge from '@/common/components/SmallBadge';
import { formatUnit } from '@/common/utils/formatters';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import { useProposalModification } from '@/features/maintenance/features/establishmentChoice/features/auctions/hooks/useProposalModification';
import { UpdateExecutionEntryEstablishmentAcceptValidationSchema } from '@/features/maintenance/features/workflows/features/executionEntry/schemas/updateExecutionEntryEstablishmentAccept.schema';
import { DiagnosticPieceData } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type';
import { WrenchIcon } from 'lucide-react';
import { Fragment } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import EditProposalModal from '../../EditProposalModal';
import ProposalAwareValuesRow from '../../ProposalAwareValuesRow';

type FormSchema = UpdateExecutionEntryEstablishmentAcceptValidationSchema
type ModificationItem = FormSchema['pieces'][number]

interface PieceRowProps {
  executionPiece: DiagnosticPieceData;
  fieldIndex: number;
}

const PieceRow = ({
  executionPiece,
  fieldIndex,
}: PieceRowProps) => {
  const { control, setValue, formState: { disabled } } = useFormContext<FormSchema>()
  const itemFormState = useWatch({ control, name: `pieces.${fieldIndex}` as Path<FormSchema> }) as ModificationItem | undefined

  const modification = useProposalModification({
    modification: {
      new_unit_price: itemFormState?.new_unit_price ? unFormatCurrency(itemFormState.new_unit_price) : null,
      new_warranty_in_days: itemFormState?.new_warranty_in_days
        ? unFormatUnknownUnit(itemFormState.new_warranty_in_days)
        : null,
    },
    unitPrice: executionPiece.unit_price,
    warrantyInDays: executionPiece.warranty_in_days,
    quantity: executionPiece.quantity,
    totalValue: executionPiece.total_value,
    disabled,
    onSave: (newUnitPrice, newWarrantyInDays) => {
      setValue(`pieces.${fieldIndex}.new_unit_price` as Path<FormSchema>, newUnitPrice as never)
      setValue(`pieces.${fieldIndex}.new_warranty_in_days` as Path<FormSchema>, newWarrantyInDays as never)
    },
  })

  const quantityDisplay = (
    <SmallBadge
      text={formatUnit(executionPiece.quantity, 'item')}
    />
  )

  return (
    <Box className="p-2 rounded-lg">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={executionPiece.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
          Placeholder={
            <ImageWithPlaceholder className="size-20 rounded-full" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-20"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPricePercentage={modification.unitPricePercentage}
              unitPriceLabel="Valor unitário"
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />

            <EditProposalModal
              title={`Editar ${executionPiece?.piece?.display_name || '--'}`}
              isOpen={modification.isOpen}
              setIsOpen={modification.handleOpenChange}
              onConfirm={modification.onConfirm}
              localUnitPrice={modification.localUnitPrice}
              setLocalUnitPrice={modification.setLocalUnitPrice}
              localWarranty={modification.localWarranty}
              setLocalWarranty={modification.setLocalWarranty}
              originalUnitPrice={modification.originalUnitPrice}
              originalTotalValue={modification.originalTotalValue}
              originalWarranty={modification.originalWarranty}
              previewTotalValue={modification.previewTotalValue}
              unitPriceLabel="Valor unitário"
              quantityDisplay={quantityDisplay}
              disabled={modification.disabled}
            />
          </div>

          <p className="font-bold text-base">
            {executionPiece?.piece?.display_name || '--'}
          </p>

          <div className="flex items-center gap-1 flex-wrap">
            {[
              executionPiece?.piece?.number,
              executionPiece?.piece?.brand?.display_name,
              executionPiece?.piece?.global_trade_item_number,
            ]
              .filter(Boolean)
              .map((value, index) => (
                <Fragment key={index}>
                  {index > 0 && (
                    <p className="text-xs">
                      |
                    </p>
                  )}

                  <p className="text-xs">
                    {value}
                  </p>
                </Fragment>
              ))}
          </div>

          <p className="text-xs">
            {executionPiece.description}
          </p>
        </div>
      </div>
    </Box>
  );
}

export default PieceRow
